import { useEffect, useMemo, useState } from 'react';

import { Icon } from '@components';
import { API_URL } from '@config';
import {
  acceptedAttachmentSupportedDocumentFormats,
  allowedImageSize,
  allowedImageSizeLabel
} from '@constants/file';
import DocumentUpload from '@features/event-registration/components/document-upload/DocumentUpload';
import useNotify from '@hooks/notify';
import useFileNameCache from '@hooks/useFileNameCache';
import generateSHA256Hash from '@utils/crypto';
import { downloadFileFromUrlUsingBlob } from '@utils/downloadFile';

import theme from '../../../../../themes/colors';

export type DocumentUploadStatus =
  | 'idle'
  | 'uploading'
  | 'success'
  | 'failed'
  | 'existing';

export interface UploadingDocument {
  file: File;
  status: DocumentUploadStatus;
  progress?: number;
}

interface SupportingDocumentUploadProps {
  initialDocuments?: File[];
  showUploader?: boolean;
  onDocumentsChange?: (documents: UploadingDocument[]) => void;
  onUploadComplete?: (file: File, success: boolean) => void;
  simulateUpload?: boolean;
  className?: string;
  accountDetailId?: string | number;
  resetKey?: string | number;
  existingDocs?: { type: string; url: string }[];
}

const SupportingDocumentUpload = ({
  initialDocuments = [],
  showUploader = true,
  onDocumentsChange,
  onUploadComplete,
  simulateUpload = true,
  className = '',
  accountDetailId,
  resetKey,
  existingDocs
}: SupportingDocumentUploadProps) => {
  const { createErrorAlert } = useNotify();
  const {
    fetchDisplayFileName,
    isRemoteFile,
    getCachedFileName,
    parseFileInfo
  } = useFileNameCache();
  const [uploadingDocuments, setUploadingDocuments] = useState<
    UploadingDocument[]
  >(
    initialDocuments.map((file) => ({
      file,
      status: 'success' as DocumentUploadStatus
    }))
  );

  const existingSupportingDocuments = useMemo(() => {
    const docs = existingDocs?.filter(
      (doc) => doc.type === 'SUPPORTING_DOCUMENT'
    );
    const updatedData = docs?.map((doc, index) => ({
      file: new File(
        [],
        decodeURIComponent(doc.url.split('/').pop()?.split('?')[0] || '') ||
          `Supporting Document ${index + 1}`
      ),
      status: 'existing' as DocumentUploadStatus,
      progress: 100,
      url: doc.url
    }));
    return updatedData;
  }, [existingDocs]);

  const [resolvedFileNames, setResolvedFileNames] = useState<
    Record<string, string>
  >({});

  const updateDocuments = (newDocs: UploadingDocument[]) => {
    setUploadingDocuments(newDocs);
  };

  const handleDocumentAdd = (file: File) => {
    if (simulateUpload) {
      const newDoc: UploadingDocument = {
        file,
        status: 'uploading',
        progress: 0
      };
      const updatedDocs = [...uploadingDocuments, newDoc];
      updateDocuments(updatedDocs);

      const interval = setInterval(() => {
        setUploadingDocuments((prev) => {
          const updated = prev.map((doc) => {
            if (doc.file === file && doc.status === 'uploading') {
              const newProgress = (doc.progress || 0) + 25;
              if (newProgress >= 100) {
                clearInterval(interval);
                onUploadComplete?.(file, true);
                return {
                  ...doc,
                  status: 'success' as DocumentUploadStatus,
                  progress: 100
                };
              }
              return { ...doc, progress: newProgress };
            }
            return doc;
          });
          return updated;
        });
      }, 500);
    } else {
      const newDoc: UploadingDocument = {
        file,
        status: 'uploading',
        progress: 0
      };
      const updatedDocs = [...uploadingDocuments, newDoc];
      updateDocuments(updatedDocs);

      let currentProgress = 0;
      const interval = setInterval(() => {
        currentProgress += 25;
        setUploadingDocuments((prev) => {
          const updated = prev.map((doc) => {
            if (doc.file === file && doc.status === 'uploading') {
              if (currentProgress >= 100) {
                clearInterval(interval);
                onUploadComplete?.(file, true);
                return {
                  ...doc,
                  status: 'success' as DocumentUploadStatus,
                  progress: 100
                };
              }
              return { ...doc, progress: currentProgress };
            }
            return doc;
          });
          return updated;
        });
      }, 200);
    }
  };

  const handleDocumentRemove = (fileToRemove: File) => {
    const updatedDocs = uploadingDocuments.filter(
      (doc) => doc.file !== fileToRemove
    );
    updateDocuments(updatedDocs);
  };

  const remoteDocsNeedingNames = useMemo(
    () =>
      uploadingDocuments
        .map((d) => d.file)
        .filter((f) => {
          if (!isRemoteFile(f.name)) return false;
          if (resolvedFileNames[f.name]) return false;
          return !getCachedFileName(f.name);
        }),
    [uploadingDocuments, resolvedFileNames, isRemoteFile, getCachedFileName]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // Fast-path: hydrate from cache without any API calls
      uploadingDocuments.forEach((doc) => {
        if (!isRemoteFile(doc.file.name)) return;
        if (resolvedFileNames[doc.file.name]) return;
        const cached = getCachedFileName(doc.file.name);
        if (!cached) return;
        setResolvedFileNames((prev) =>
          prev[doc.file.name] ? prev : { ...prev, [doc.file.name]: cached }
        );
      });

      if (remoteDocsNeedingNames.length === 0) return;

      await Promise.all(
        remoteDocsNeedingNames.map(async (file) => {
          const resolved = await fetchDisplayFileName(file.name);
          if (cancelled || !resolved) return;
          setResolvedFileNames((prev) =>
            prev[file.name] ? prev : { ...prev, [file.name]: resolved }
          );
        })
      );
    };

    run().catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [
    remoteDocsNeedingNames,
    fetchDisplayFileName,
    uploadingDocuments,
    resolvedFileNames,
    isRemoteFile,
    getCachedFileName
  ]);

  const handleExistingDocDownload = async (url: string, name: string) => {
    try {
      await downloadFileFromUrlUsingBlob(url, name);
    } catch {
      createErrorAlert('Error downloading file');
    }
  };

  const handleDocumentDownload = async (file: File) => {
    const { accessKey, fileId } = parseFileInfo(file.name);

    const isLocalFile = !isRemoteFile(file.name);

    if (isLocalFile) {
      const blobUrl = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      return;
    }

    if (!accountDetailId || !accessKey || !fileId) return;

    try {
      const signatureInput = `${fileId}|${accessKey}`;
      const signature = await generateSHA256Hash(signatureInput);

      const redirectUrl = `${API_URL}/_files/download/${fileId}/${accessKey}?signature=${signature}&isExport=false`;
      const presignedResponse = await fetch(redirectUrl).then((res) =>
        res.json()
      );

      if (!presignedResponse) {
        createErrorAlert('Error downloading file');
        return;
      }

      const presignedUrl =
        typeof presignedResponse === 'string'
          ? presignedResponse
          : presignedResponse?.url || presignedResponse?.presignedUrl;

      if (!presignedUrl) {
        createErrorAlert('Error downloading file');
        return;
      }

      const downloadName =
        resolvedFileNames[file.name] || getCachedFileName(file.name) || fileId;
      await downloadFileFromUrlUsingBlob(presignedUrl, downloadName);
    } catch (error) {
      createErrorAlert('Error downloading file');
    }
  };

  useEffect(() => {
    // Reset internal state when resetKey changes (e.g., modal close/open)
    setUploadingDocuments(
      initialDocuments.map((file) => ({
        file,
        status: 'success' as DocumentUploadStatus
      }))
    );
    setResolvedFileNames({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    onDocumentsChange?.(uploadingDocuments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadingDocuments]);

  return (
    <div className={className}>
      {showUploader && (
        <DocumentUpload
          key={resetKey}
          label="supporting-documents-upload"
          handleAdd={handleDocumentAdd}
          handleRemove={() => {}}
          allowedFileSize={allowedImageSize}
          allowedFileSizeLabel={allowedImageSizeLabel}
          allowedDocumentTypes={[
            'application/pdf',
            'image/png',
            'image/jpeg',
            'image/jpg'
          ]}
          acceptedDocInputTypes={acceptedAttachmentSupportedDocumentFormats}
          onError={() => {}}
          className="!h-18 !flex-row !justify-start gap-x-2 !p-4"
          subText="Supported file: PNG, JPG and PDF (max. 5MB)"
          iconClassName="!mb-0"
          hideSuccessState
          errorStyles="!bg-transparent !border-none !p-0 !text-sm !text-error"
          customClassName="!mr-auto"
        />
      )}

      <div className={`${showUploader ? 'mt-2' : ''} space-y-2`}>
        {[...uploadingDocuments, ...(existingSupportingDocuments || [])].map(
          (doc, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`doc-${index}-${doc.file.name}-${doc.file.size}`}
              className="mt-2 flex items-center justify-between rounded-lg border border-grey3 p-3"
            >
              <div className="flex items-center gap-3">
                {/* Icon based on status */}
                {doc.status === 'uploading' && (
                  <div className="relative flex size-8 items-center justify-center">
                    <svg className="size-8 -rotate-90" viewBox="0 0 32 32">
                      <circle
                        cx="16"
                        cy="16"
                        r="14"
                        fill="none"
                        stroke={theme.grey3}
                        strokeWidth="3"
                      />
                      <circle
                        cx="16"
                        cy="16"
                        r="14"
                        fill="none"
                        stroke={theme.primary}
                        strokeWidth="3"
                        strokeDasharray={`${(doc.progress || 0) * 0.88} 88`}
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                )}
                {doc.status === 'failed' && (
                  <div className="flex size-8 items-center justify-center">
                    <Icon
                      name="alert-circle"
                      size="medium"
                      stroke={theme.error}
                    />
                  </div>
                )}
                {(doc.status === 'success' || doc.status === 'existing') && (
                  <Icon
                    name={
                      doc.file.type.startsWith('image/')
                        ? 'image-placeholder'
                        : 'file-icon-grey'
                    }
                    size="24"
                    stroke={theme.grey}
                  />
                )}

                <div className="flex flex-col">
                  <span className="max-w-80 truncate text-sm font-medium text-cofeeNeutral10">
                    {(() => {
                      const displayName = isRemoteFile(doc.file.name)
                        ? resolvedFileNames[doc.file.name] ||
                          getCachedFileName(doc.file.name) ||
                          parseFileInfo(doc.file.name).fileId
                        : doc.file.name;
                      return displayName.length > 20 &&
                        doc.status !== 'existing'
                        ? `${displayName.slice(0, 20)}...`
                        : displayName;
                    })()}
                  </span>
                  {doc.status === 'uploading' && (
                    <span className="text-xs text-primary">
                      {doc.progress}% Uploading...
                    </span>
                  )}
                  {doc.status === 'failed' && (
                    <span className="text-xs text-error">Failed to upload</span>
                  )}
                  {doc.status === 'success' && !isRemoteFile(doc.file.name) ? (
                    <span className="text-greyDisabled text-xs text-grey">
                      {(doc.file.size / 1024).toFixed(2)}KB
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Action icon based on status */}
              {(doc.status === 'uploading' || doc.status === 'failed') && (
                <Icon
                  name="close-lite"
                  size="12"
                  className="mr-1 cursor-pointer"
                  stroke={theme.grey}
                  onClick={() => handleDocumentRemove(doc.file)}
                />
              )}
              {doc.status === 'existing' && (doc as any).url && (
                <Icon
                  name="download"
                  size="medium"
                  className="cursor-pointer"
                  stroke={theme.grey}
                  onClick={() =>
                    handleExistingDocDownload((doc as any).url, doc.file.name)
                  }
                />
              )}
              {doc.status === 'success' && (
                <div className="flex items-center gap-2">
                  {isRemoteFile(doc.file.name) ? (
                    <Icon
                      name="download"
                      size="medium"
                      className="cursor-pointer"
                      stroke={theme.grey}
                      onClick={() => handleDocumentDownload(doc.file)}
                    />
                  ) : (
                    <Icon
                      name="close-lite"
                      size="12"
                      className="mr-1 cursor-pointer"
                      stroke={theme.grey}
                      onClick={() => handleDocumentRemove(doc.file)}
                    />
                  )}
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SupportingDocumentUpload;
